import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "../api.js";

// ENT-06 "tenant shown in every ZD screen" (S-062, ST-100, BD-61). The shell holds the selected
// tenant for every ZD screen. A screen that works on one tenant reads it with useSelectedTenant()
// and passes its id to the api (?tenant=); the api answers with the tenant it used, and the
// TenantBanner shows it on every screen. With no tenant selected, screens show every tenant and
// name the tenant on each row.

export interface TenantChoice {
  id: string;
  name: string;
  status: string;
}

interface TenantState {
  tenants: TenantChoice[] | undefined;
  loadFailed: boolean;
  selected: TenantChoice | null;
  select: (id: string | null) => void;
}

const Ctx = createContext<TenantState>({ tenants: [], loadFailed: false, selected: null, select: () => undefined });

export const useSelectedTenant = () => useContext(Ctx);

export function TenantProvider({ children, initialTenantId = null }: { children: ReactNode; initialTenantId?: string | null }) {
  const [tenants, setTenants] = useState<TenantChoice[] | undefined>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(initialTenantId);

  useEffect(() => {
    // The Z02 list (S-041) is the source of tenant names.
    void api<{ tenants: TenantChoice[] }>("/v1/zd/tenants").then((r) => {
      if (r.ok) setTenants(r.data.tenants.map(({ id, name, status }) => ({ id, name, status })));
      else setLoadFailed(true);
    });
  }, []);

  const selected = (selectedId && tenants?.find((x) => x.id === selectedId)) || null;
  return <Ctx.Provider value={{ tenants, loadFailed, selected, select: setSelectedId }}>{children}</Ctx.Provider>;
}

/** The tenant picker and the banner naming the tenant, shown above every ZD screen. */
export function TenantBar() {
  const { tenants, loadFailed, selected, select } = useSelectedTenant();
  return (
    <div className="tenant-bar">
      <label>
        {t("console.common.tenant_label")}
        <select name="tenant" value={selected?.id ?? ""} onChange={(e) => select(e.target.value || null)} disabled={!tenants}>
          <option value="">{t("console.common.tenant_all")}</option>
          {tenants?.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
      </label>
      <TenantBanner name={selected?.name ?? null} />
      {loadFailed && (
        <p role="alert" className="error">
          {t("console.common.tenants_load_failed")}
        </p>
      )}
    </div>
  );
}

/** Names the tenant the screens are showing, or says that every row names its tenant. */
export function TenantBanner({ name }: { name: string | null }) {
  return (
    <p className="tenant-banner" data-testid="tenant-banner">
      {name ? (
        <>
          {t("console.common.tenant_working_on")} <strong>{name}</strong>
        </>
      ) : (
        t("console.common.tenant_all_note")
      )}
    </p>
  );
}
